import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import PermissionChecker from '../../../components/common/PermissionChecker';
import CustomTable from '../../../components/common/CustomTable';
import { columns } from './columns';
import { styles } from './styles';
import {
    useFetchRecruitmentPolicyMutation,
    useResyncAllPositionsWithAllPoliciesMutation,
} from '../../../api/recruitmentPolicy';

const RecruitmentPolicy = () => {
    const [recruitmentPolicy, setRecruitmentPolicy] = useState([]);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useSelector((state) => state.session);
    const [openDialog, setOpenDialog] = useState(false);

    const [fetchRecruitmentPolicy, { isLoading }] = useFetchRecruitmentPolicyMutation();
    const [resyncAllPositionsWithAllPolicies, { isLoading: isResyncing }] =
        useResyncAllPositionsWithAllPoliciesMutation();

    const fetchData = useCallback(
        async (isRefresh) => {
            try {
                const bodyFormData = new FormData();
                bodyFormData.append('refresh', isRefresh);
                const response = await fetchRecruitmentPolicy(bodyFormData).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setRecruitmentPolicy(response?.recruitmentPolicies || []);
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [fetchRecruitmentPolicy, enqueueSnackbar],
    );

    useEffect(() => {
        fetchData(false);
    }, [fetchData]);

    const handleRowClick = (id) => {
        navigate(`/recruitmentPolicies/${id}`);
    };

    const handleRefresh = () => {
        fetchData(true);
    };

    const handleBulkResync = async () => {
        if (isResyncing) return;
        const bodyFormData = new FormData();
        bodyFormData.append('companyId', user.companyDBID);
        try {
            const response = await resyncAllPositionsWithAllPolicies(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        } finally {
            setOpenDialog(false);
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Recruitment Policy</Typography>
                <Box>
                    <PermissionChecker
                        requiredPermissions={[
                            'ROLE_BASE_PERMISSION_RECRUIT_INDEX_RESYNC_ALL_POSITIONS_WITH_ALL_RECRUITMENT_POLICIES',
                        ]}
                        noAuthRedirect={false}
                    >
                        <Button
                            color="primary"
                            sx={{
                                mr: '15px',
                            }}
                            onClick={() => setOpenDialog(true)}
                            size="large"
                            startIcon={<AutorenewIcon fontSize="small" />}
                            variant="contained"
                        >
                            Bulk Resync
                        </Button>
                    </PermissionChecker>
                    <PermissionChecker
                        requiredPermissions={[
                            'ROLE_BASE_PERMISSION_RECRUIT_INDEX_GET_RECRUITMENT_POLICY_DETAILS_FOR_CANVAS',
                        ]}
                        noAuthRedirect={false}
                    >
                        <Button
                            color="primary"
                            onClick={handleRefresh}
                            size="large"
                            startIcon={<RefreshIcon fontSize="small" />}
                            variant="contained"
                        >
                            Refresh
                        </Button>
                    </PermissionChecker>
                </Box>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <CustomTable
                            isLoading={isLoading}
                            onRowClick={handleRowClick}
                            columns={columns}
                            rows={recruitmentPolicy}
                            showRowSelector={false}
                            showPagignation={false}
                            keyColumn="id"
                            action={() => fetchData(false)}
                        />
                    </CardContent>
                </Card>
            </Box>
            <Dialog open={openDialog}>
                <DialogContent sx={styles.tableMenuDialog}>
                    <Box sx={styles.tableMenuDialog}>
                        <DialogTitle>Resync Bulk Recruitment Policy</DialogTitle>
                        <DialogContentText>
                            Are you sure you want to resync bulk Recruitment Policy with Positions?
                        </DialogContentText>
                    </Box>
                    <DialogActions>
                        <Button
                            size="medium"
                            variant="text"
                            onClick={() => setOpenDialog(false)}
                            disabled={isResyncing}
                        >
                            No
                        </Button>
                        <Button
                            size="medium"
                            variant="contained"
                            onClick={handleBulkResync}
                            startIcon={isResyncing ? <CircularProgress color="inherit" size="1.2rem" /> : <></>}
                        >
                            Yes
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
export default RecruitmentPolicy;
